// Umami, our own analytics (docs/ANALYTICS.md, issue 1601). The tracker is served from this origin at /a/script.js
// (nginx proxies it and /a/api/send to Umami), so the page's CSP needs nothing new. `track` sends a custom event;
// `beforeSend` is the tracker's `data-before-send` hook (index.html): every beacon passes through it on its way out.
//
// - An address is reported as its route: no query, no hash, and a table's campaign id or a shared link's token is `:id`.
// - A page view at the address just counted is not sent again.
// - Every beacon carries the table's game system as its `tag` once a table has been read.
// - The page title never leaves: on a table it is the campaign's name.
//
// Events are kinds, counts and registry keys, never names, text, ids, chat or campaign content
// (`analyticsEvents.test.ts` checks every call).

type Data = Record<string, string | number>;
interface Umami { track: (name: string, data?: Data) => void }

/** A custom event; nothing where there is no tracker (local, tests, a browser that opted out). */
export function track(name: string, data?: Data): void {
  const u = (window as unknown as { umami?: Umami }).umami;
  if (u && typeof u.track === 'function') u.track(name, data);
}

/** The global the tracker's `data-before-send` names. */
export const BEFORE_SEND = 'tdtAnalyticsBeforeSend';

let system: string | null = null;
/** The table's game system slug, the beacons' tag; null when no table is read. */
export const setAnalyticsSystem = (slug: string | null | undefined): void => { system = slug ? String(slug).slice(0, 50) : null; };

const ID_ROUTES: [RegExp, string][] = [
  [/^\/t\/[^/]+/, '/t/:id'],
  [/^\/library\/shared\/[^/]+/, '/library/shared/:id'],
];

/** `href` as its route: the query and hash dropped, an id in the path as `:id`. Anything unreadable as it came. */
export function canonicalUrl<T>(href: T): T | string {
  if (typeof href !== 'string') return href;
  let u: URL;
  try { u = new URL(href); } catch { return href; }
  let path = u.pathname;
  for (const [re, to] of ID_ROUTES) path = path.replace(re, to);
  return `${u.origin}${path}`;
}

/** A referrer: this origin's as its route, an outside one as its origin and path. */
function referrerOf(href: unknown, origin: string | null): unknown {
  if (typeof href !== 'string' || !href) return href;
  let u: URL;
  try { u = new URL(href); } catch { return href; }
  return u.origin === origin ? canonicalUrl(href) : `${u.origin}${u.pathname}`;
}

let lastView: unknown = null;
const opened = new Set<string>();
let openedAt: number | null = null;
let lengthSent = false;

/** For tests: forget the page's views, tables, system and session. */
export function resetAnalytics(): void {
  lastView = null; system = null; opened.clear(); openedAt = null; lengthSent = false;
}

export function beforeSend(type: string, payload: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return payload;
  const url = canonicalUrl(payload.url);
  let origin: string | null = null;
  try { origin = new URL(String(payload.url)).origin; } catch { /* the referrer is kept as it came */ }
  if (type === 'event' && !payload.name) {
    if (url === lastView) return null;
    lastView = url;
  }
  const out: Record<string, unknown> = { ...payload, url, referrer: referrerOf(payload.referrer, origin) };
  delete out.title;
  if (system) out.tag = system; else delete out.tag;
  return out;
}

/** A table opened (joined), once per table per page load; the first starts the session's clock. */
export function tableOpened(campaignId: string, role: string): void {
  if (opened.has(campaignId)) return;
  opened.add(campaignId);
  track('table-open', { role });
  if (openedAt === null) openedAt = Date.now();
}

/** The minutes since the first table opened, once per page load, as the page goes. */
function sessionEnded(): void {
  if (openedAt === null || lengthSent) return;
  lengthSent = true;
  track('session-length', { minutes: Math.round((Date.now() - openedAt) / 60_000) });
}

/** An act the table accepted, as the event it counts (kinds and counts only); most acts count nothing. */
export function trackAct(body: unknown): void {
  if (!body || typeof body !== 'object') return;
  const b = body as { kind?: unknown; quantity?: unknown; to?: unknown; object?: { kind?: unknown; asset?: unknown } };
  switch (b.kind) {
    case 'roll.request':
      track('dice-roll', { count: typeof b.quantity === 'number' ? b.quantity : 1 });
      break;
    case 'encounter.start': track('encounter-start'); break;
    case 'handout.send': track('handout-send'); break;
    case 'encounter.deal': track('cards-deal', { to: 'initiative' }); break;
    case 'deck.draw':
      // A card drawn into one's own hand is a draw, not a deal.
      if (b.to === 'table') track('cards-deal', { to: 'table' });
      else if (typeof b.to === 'string' && b.to) track('cards-deal', { to: 'player' });
      break;
    case 'object.add':
      // A library picture put on the table as a token; scenery and the Properties panel's markers are not counted.
      if (b.object?.kind === 'token' && typeof b.object.asset === 'string') track('token-place', { from: 'library' });
      break;
    default:
  }
}

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>)[BEFORE_SEND] = beforeSend;
  window.addEventListener('pagehide', sessionEnded);
}
